import React, { useEffect, useState } from "react"
import { format } from "date-fns"
import Cookies from "js-cookie";
import { CheckCircle } from "react-feather"
import { __ } from "@wordpress/i18n";
import "./admin/vars.scss";
import "./calendar.scss"

// Convert PHP format → date-fns format (basic mapping)
function phpToDateFns(phpFormat) {
    return phpFormat
        .replace("d", "dd")
        .replace("j", "d")
        .replace("m", "MM")
        .replace("n", "M")
        .replace("Y", "yyyy")
        .replace("y", "yy")
        .replace("F", "MMMM")
        .replace("M", "MMM")
}

// For time format
function phpToTimeFns(phpFormat) {
    return phpFormat
        .replace("H", "HH") 
        .replace("G", "H")
        .replace("h", "hh")
        .replace("g", "h")
        .replace("i", "mm")
        .replace("s", "ss")
        .replace("a", "aa")
        .replace("A", "aa") // date-fns uses lowercase/uppercase by locale
}

function formatNumericDate(date) {
    // Use a function for day name translation
    function getTranslatedDayName(dayIndex) {
        switch (dayIndex) {
            case 1: return __("Monday", "next-days-booking");
            case 2: return __("Tuesday", "next-days-booking");
            case 3: return __("Wednesday", "next-days-booking");
            case 4: return __("Thursday", "next-days-booking");
            case 5: return __("Friday", "next-days-booking");
            case 6: return __("Saturday", "next-days-booking");
            case 0: return __("Sunday", "next-days-booking");
            default: return "";
        }
    }
    const dayName = getTranslatedDayName(date.getDay());
    const day = date.getDate()          // 1–31
    const month = date.getMonth() + 1   // 1–12
    const year = date.getFullYear()     // 4-digit year
    return `${dayName}, ${day}/${month}/${year}`
}

function getNextDays(count) {
    const days = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < count; i++) {
        const d = new Date(today)
        d.setDate(today.getDate() + i)
        days.push({
            date: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`, // machine-readable
            label: formatNumericDate(d), // human-readable
            isDisabled: d.getDay() === 0 || (i === 0 && new Date().getHours() >= 17) // true if Sunday or today after 18:00
        })
    }

    return days
}

function safeDate(input) {
  const [y, m, d] = input.split("-");
  return new Date(Number(y), Number(m) - 1, Number(d));
}

function getTimeSlots(stepMinutes, timeFormat, selectedDay) {
    const slots = []
    const start = safeDate(selectedDay)
    
    start.setHours(8, 30, 0, 0)

    const end = safeDate(selectedDay)
    if (start.getDay() === 6) {
        end.setHours(12, 30, 0, 0);
    } else {
        end.setHours(17, 40, 0, 0);
    }

    let current = new Date(start)
    while (current <= end) {
        slots.push(format(current, phpToTimeFns(timeFormat)))
        current = new Date(current.getTime() + stepMinutes * 60000)
    }
    return slots
}

async function getTakenSlots(day) {
    try {
        const response = await fetch(
            `${ndbBookingCalendar.root}ndb/v1/get-bookings-by-day`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-WP-Nonce": ndbBookingCalendar.nonce,
                },
                body: JSON.stringify({ day }),
            }
        )

        if (!response.ok) {
            throw new Error("Failed to fetch taken slots")
        }

        const isDay = safeDate(day);
        const result = await response.json()
        return result.map((booking) => booking.time)
    } catch (err) {
        console.error(err)
        return []
    }
}

function Step1({ onSelect }) {
    const bookingDays = ndbBookingCalendar.settings.next_days.booking_days
    const dateFormat = ndbBookingCalendar.settings.date_format

    const days = getNextDays(bookingDays, dateFormat)

    return (
        <div>
            <h3>{__("Select a day:", "next-days-booking")}</h3>
            <div className='ndb-day-selector'>
                {days.map((day, i) => {
                    let displayLabel = day.label
                    if (i === 0) displayLabel = __("Today", "next-days-booking")
                    else if (i === 1) displayLabel = __("Tomorrow", "next-days-booking")
                    return (
                        <button
                            key={i}
                            onClick={() => onSelect(day.date)} disabled={day.isDisabled}
                        >
                            {displayLabel}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

function is12HourFormat(locale = navigator.language) {
  const formatter = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
  const parts = formatter.formatToParts(new Date());
  return parts.some(part => part.type === 'dayPeriod'); // "AM"/"PM"
}


function Step2({ onSelect, onBack, selectedDay }) {
    const stepMinutes = ndbBookingCalendar.settings.next_days.booking_length
    const timeFormat = ndbBookingCalendar.settings.time_format

    const [takenSlots, setTakenSlots] = useState([])
    const [loading, setLoading] = useState(true)
    const slots = getTimeSlots(stepMinutes, timeFormat, selectedDay)

    // Filter out past times if selectedDay is today
    const filteredSlots = React.useMemo(() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const selected = safeDate(selectedDay)
        selected.setHours(0, 0, 0, 0)
        if (selected.getTime() !== today.getTime()) {
            return slots
        }
        // Only show slots that are in the future for today
        const now = new Date()
        return slots.filter((slot) => {
            // Parse slot time to Date object for today
            let hour, minute;
            if (is12HourFormat()) {
                // Handle 12-hour format with AM/PM
                const [time, ampm] = slot.split(" ");
                [hour, minute] = time.split(":");
                hour = parseInt(hour, 10);
                minute = parseInt(minute, 10);
                if (ampm === "PM" && hour < 12) hour += 12;
                if (ampm === "AM" && hour === 12) hour = 0;
            } else {
                [hour, minute] = slot.split(":");
                hour = parseInt(hour, 10);
                minute = parseInt(minute, 10);
            }
            const slotDate = new Date(today)
            slotDate.setHours(hour, minute, 0, 0)
            return slotDate > now
        })
    }, [slots, selectedDay])

    React.useEffect(() => {
        let isMounted = true
        setLoading(true)
        getTakenSlots(selectedDay).then((slots) => {
            if (isMounted) {
                setTakenSlots(slots)
                setLoading(false)
            }
        })
        return () => { isMounted = false }
    }, [selectedDay])

    return (
        <div>
            <h3>{__("Select a time:", "next-days-booking")}</h3>
            {loading ? (
                <div>{__("Loading slots...", "next-days-booking")}</div>
            ) : (
                <div className='ndb-time-selector'>
                    {filteredSlots.length === 0 ? (
                        <div>{__("No available times for today.", "next-days-booking")}</div>
                    ) : (
                        filteredSlots.map((slot, i) => (
                            <button
                                key={i}
                                onClick={() => onSelect(slot)}
                                disabled={takenSlots.includes(slot)}
                            >
                                {slot}
                            </button>
                        ))
                    )}
                </div>
            )}
            <div className="ndb-form-buttons">
                <button onClick={onBack} className="ndb-button ndb-button-secondary">
                    {__("Back", "next-days-booking")}
                </button>
            </div>
        </div>
    )
}

function Step3({ onSubmit, onBack }) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        regNumber: "",
    })

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        onSubmit(formData)
    }

    return (
        <div>
            <h3>{__("Enter your details:", "next-days-booking")}</h3>
            <form onSubmit={handleSubmit}>
                <div className="ndb-form-fields">
                    <h4>{__("Name (required)", "next-days-booking")}</h4>
                    <input
                        name="name"
                        type="text"
                        placeholder={__("Name", "next-days-booking")}
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="ndb-input"
                    />
                    <h4>{__("Email", "next-days-booking")}</h4>
                    <input
                        name="email"
                        type="email"
                        placeholder={__("Email", "next-days-booking")}
                        value={formData.email}
                        onChange={handleChange}
                        className="ndb-input"
                    />
                    <h4>{__("Phone (required)", "next-days-booking")}</h4>
                    <input
                        name="phone"
                        type="tel"
                        placeholder={__("Phone", "next-days-booking")}
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="ndb-input"
                    />
                    <h4>{__("Registration number (required)", "next-days-booking")}</h4>
                    <input
                        name="regNumber"
                        type="text"
                        placeholder={__("Registration Number", "next-days-booking")}
                        value={formData.regNumber}
                        onChange={handleChange}
                        required
                        className="ndb-input"
                    />
                </div>

                <div className="ndb-form-buttons">
                    <button
                        type="submit"
                        className="ndb-button"
                    >
                        {__("Confirm", "next-days-booking")}
                    </button>

                    <button
                        type="button"
                        onClick={onBack}
                        className="ndb-button ndb-button-secondary"
                    >
                        {__("Back", "next-days-booking")}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default function BookingCalendar() {
    const [step, setStep] = useState(1)
    const [selectedDay, setSelectedDay] = useState(null)
    const [selectedTime, setSelectedTime] = useState(null)
    const [bookingId, setBookingId] = useState(null)
    const [bookingDataState, setBookingDataState] = useState(null)

    // Cookies.remove("ndb-cookie"); // For testing only - remove in production

    useEffect(() => {
        // Check for existing cookie
        const cookie = Cookies.get("ndb-cookie");

        if (cookie) {
            setBookingId(cookie);
        }
    }, []);

    const handleDaySelect = (day) => {
        setSelectedDay(day)
        setStep(2)
    }

    const handleTimeSelect = (time) => {
        setSelectedTime(time)
        setStep(3)
    }

    const bookingData = async () => {
        if ( !bookingId ) return null;

        const response = await fetch(`${ndbBookingCalendar.root}ndb/v1/get-booking-by-id`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": ndbBookingCalendar.nonce,
            },
            body: JSON.stringify({ id: bookingId }),
        })

        if (!response.ok) {
            setBookingId(null);
            // Cookies.remove("ndb-cookie");
            throw new Error("Failed to fetch bookings")
        }

        const result = await response.json()
        return result
    }

    useEffect(() => {
        if (bookingId) {
            bookingData().then(data => {
                setBookingDataState(data);
            }).catch(err => {
                console.error(err);
            });
        }
    }, [bookingId]);

    const handleFormSubmit = async (formData) => {
        const payload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            regNumber: formData.regNumber,
            day: selectedDay,
            time: selectedTime,
            comment: ''
        }

        try {
            const response = await fetch(`${ndbBookingCalendar.root}ndb/v1/create-booking`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-WP-Nonce": ndbBookingCalendar.nonce,
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                throw new Error("Failed to create booking")
            }

            const result = await response.json()

            // Calculate days until selectedDay
            const today = new Date();
            const selected = new Date(selectedDay);
            selected.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const diffDays = Math.max(1, Math.ceil((selected - today) / (1000 * 60 * 60 * 24)));

            Cookies.set("ndb-cookie", result.id, { expires: diffDays });

            // Read cookie
            const cookie = Cookies.get("ndb-cookie");

            setBookingId(result.id);

            // reset state
            setStep(1)
            setSelectedDay(null)
            setSelectedTime(null)
        } catch (err) {
            alert(`Error: ${err.message}`)
        }
    }

    // Helper to get readable day label
    function getReadableDayLabel(dayStr) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const selected = safeDate(dayStr);
        selected.setHours(0, 0, 0, 0);

        if (selected.getTime() === today.getTime()) return "Today";
        if (selected.getTime() === tomorrow.getTime()) return "Tomorrow";
        return formatNumericDate(selected);
    }

    // Helper to check if booking time has passed
    function isBookingTimePassed(booking) {
        if (!booking || !booking.day || !booking.time) return false;
        // booking.day is "YYYY-M-D" or "YYYY-MM-DD"
        // booking.time is "HH:mm" or similar
        const [year, month, day] = booking.day.split("-").map(Number);
        const [hour, minute] = booking.time.split(":").map(Number);
        const bookingDate = new Date(year, month - 1, day, hour, minute, 0, 0);
        return new Date() > bookingDate;
    }

    const showForm = !bookingId || (bookingDataState && isBookingTimePassed(bookingDataState));

    return (
        <div>
            {bookingId && bookingDataState && !isBookingTimePassed(bookingDataState) ? (
                <div className="ndb-info-box">
                    <p className="ndb-info-box-text">
                        <CheckCircle size={22} color="#28a745" />  {__( "Booking successfull", "next-days-booking" )} (ID: <strong>{bookingId}</strong>).
                    </p>

                    <ul>
                        <li>{__("Name", "next-days-booking")}: <strong>{bookingDataState ? bookingDataState.name : ''}</strong></li>
                        <li>{__("Email", "next-days-booking")}: <strong>{bookingDataState && bookingDataState.email ? bookingDataState.email : '—'}</strong></li>
                        <li>{__("Phone", "next-days-booking")}: <strong>{bookingDataState ? bookingDataState.phone : ''}</strong></li>
                        <li>{__("Registration Number", "next-days-booking")}: <strong>{bookingDataState ? bookingDataState.regNumber.toUpperCase() : ''}</strong></li>
                        <li>{__("Day", "next-days-booking")}: <strong>{bookingDataState ? getReadableDayLabel(bookingDataState.day) : ''}</strong></li>
                        <li>{__("Time", "next-days-booking")}: <strong>{bookingDataState ? bookingDataState.time : ''}</strong></li>
                    </ul>

                    <p>
                        {__("Please contact us if you need to modify or cancel your booking.", "next-days-booking")}
                    </p>
                </div>
            ) : (
                <>
                    {step === 1 && <Step1 onSelect={handleDaySelect} />}
                    {step === 2 && (
                        <Step2
                            onSelect={handleTimeSelect}
                            onBack={() => setStep(1)}
                            selectedDay={selectedDay}
                        />
                    )}
                    {step === 3 && (
                        <Step3
                            onSubmit={handleFormSubmit}
                            onBack={() => setStep(2)}
                        />
                    )}
                </>
            )}
        </div>
    )
}
